/**
 * ThemeSwitcherDemo Component
 *
 * Development-only component demonstrating theme switching capability.
 * This component showcases how tenants can customize their brand colors.
 *
 * IMPORTANT: This is for demonstration purposes only.
 * Remove or hide in production builds.
 */

import React, { useState } from "react";
import { useTenantTheme } from "@quiz-commerce/design-system";

interface PresetTheme {
  name: string;
  description: string;
  theme: {
    primaryColor?: string;
    successColor?: string;
    backgroundColor?: string;
    textColor?: string;
  };
}

/**
 * Preset theme configurations for quick testing
 */
const PRESET_THEMES: PresetTheme[] = [
  {
    name: "Default (Blue)",
    description: "Original QuizNudge branding",
    theme: {},
  },
  {
    name: "Vibrant Orange",
    description: "Warm, energetic brand",
    theme: {
      primaryColor: "#FF5722",
      successColor: "#4CAF50",
    },
  },
  {
    name: "Professional Purple",
    description: "Sophisticated, premium feel",
    theme: {
      primaryColor: "#9C27B0",
      successColor: "#00BCD4",
    },
  },
  {
    name: "Nature Green",
    description: "Organic, eco-friendly brand",
    theme: {
      primaryColor: "#4CAF50",
      successColor: "#8BC34A",
    },
  },
  {
    name: "Bold Red",
    description: "High-energy, attention-grabbing",
    theme: {
      primaryColor: "#F44336",
      successColor: "#FF9800",
    },
  },
  {
    name: "Calm Teal",
    description: "Relaxed, trustworthy brand",
    theme: {
      primaryColor: "#009688",
      successColor: "#00BCD4",
    },
  },
];

/**
 * ThemeSwitcherDemo Component
 *
 * Renders a floating panel with theme preset buttons.
 * Only shown in development mode.
 */
export const ThemeSwitcherDemo: React.FC = () => {
  const { setTheme } = useTenantTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTheme, setActiveTheme] = useState(0);

  // Only show in development
  if (import.meta.env.PROD) {
    return null;
  }

  const handleThemeChange = (theme: PresetTheme["theme"], index: number) => {
    setTheme(theme);
    setActiveTheme(index);
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: "20px",
        right: "20px",
        zIndex: 9999,
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}
    >
      {/* Collapsed button */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          style={{
            backgroundColor:
              "var(--semantic-color-interactive-primary-default)",
            color: "var(--semantic-color-text-inverse)",
            border: "none",
            borderRadius: "50%",
            width: "56px",
            height: "56px",
            fontSize: "24px",
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "transform 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
          }}
          aria-label="Open theme switcher"
          title="Theme Switcher Demo"
        >
          🎨
        </button>
      )}

      {/* Expanded panel */}
      {isExpanded && (
        <div
          style={{
            backgroundColor: "var(--semantic-color-background-primary)",
            border: "1px solid var(--semantic-color-border-default)",
            borderRadius: "12px",
            padding: "20px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            minWidth: "320px",
            maxWidth: "400px",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <div>
              <h3
                style={{
                  margin: "0 0 4px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "var(--semantic-color-text-primary)",
                }}
              >
                🎨 Theme Switcher
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: "12px",
                  color: "var(--semantic-color-text-secondary)",
                }}
              >
                Development Mode Only
              </p>
            </div>
            <button
              onClick={() => setIsExpanded(false)}
              style={{
                background: "none",
                border: "none",
                fontSize: "24px",
                cursor: "pointer",
                padding: "4px",
                color: "var(--semantic-color-text-secondary)",
              }}
              aria-label="Close theme switcher"
            >
              ✕
            </button>
          </div>

          {/* Theme presets */}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {PRESET_THEMES.map((preset, index) => (
              <button
                key={preset.name}
                onClick={() => handleThemeChange(preset.theme, index)}
                style={{
                  padding: "12px",
                  border: `2px solid ${
                    activeTheme === index
                      ? "var(--semantic-color-interactive-primary-default)"
                      : "var(--semantic-color-border-subtle)"
                  }`,
                  borderRadius: "8px",
                  backgroundColor:
                    activeTheme === index
                      ? "var(--semantic-color-background-secondary)"
                      : "var(--semantic-color-background-primary)",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  if (activeTheme !== index) {
                    e.currentTarget.style.borderColor =
                      "var(--semantic-color-border-default)";
                    e.currentTarget.style.backgroundColor =
                      "var(--semantic-color-background-secondary)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTheme !== index) {
                    e.currentTarget.style.borderColor =
                      "var(--semantic-color-border-subtle)";
                    e.currentTarget.style.backgroundColor =
                      "var(--semantic-color-background-primary)";
                  }
                }}
              >
                <div
                  style={{
                    fontWeight: activeTheme === index ? "600" : "500",
                    fontSize: "14px",
                    color: "var(--semantic-color-text-primary)",
                    marginBottom: "2px",
                  }}
                >
                  {preset.name}
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "var(--semantic-color-text-secondary)",
                  }}
                >
                  {preset.description}
                </div>
                {preset.theme.primaryColor && (
                  <div
                    style={{
                      marginTop: "8px",
                      display: "flex",
                      gap: "4px",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        width: "20px",
                        height: "20px",
                        borderRadius: "4px",
                        backgroundColor: preset.theme.primaryColor,
                        border: "1px solid rgba(0,0,0,0.1)",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "11px",
                        color: "var(--semantic-color-text-tertiary)",
                        fontFamily: "monospace",
                      }}
                    >
                      {preset.theme.primaryColor}
                    </span>
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Info footer */}
          <div
            style={{
              marginTop: "16px",
              padding: "12px",
              backgroundColor: "var(--semantic-color-feedback-info-background)",
              border: "1px solid var(--semantic-color-feedback-info-border)",
              borderRadius: "6px",
              fontSize: "12px",
              color: "var(--semantic-color-feedback-info-text)",
            }}
          >
            💡 <strong>Tip:</strong> Theme changes apply instantly to all
            components using token variables. Check QuizNavigation buttons to
            see the effect!
          </div>
        </div>
      )}
    </div>
  );
};
